import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { getHttpHeader } from '../../../../util/FetchTools';
import { getUnifiedErrorObject } from '../../../../util/ErrorUnifier';
import ipaddr from 'ipaddr.js';

const STREAM_MIN_LEN = 11;
const DEBOUNCE_MS = 500;

export const REFERRER_SPECIAL_OPTIONS = [
   { value: '__ref_no_http__', label: `not starting with 'http'` },
   { value: '__ref_file__', label: `starting with 'file://'` },
   { value: '__ref_dash__', label: `'-'` },
   { value: '__ref_null__', label: `'null'` },
];

const VALIDATION_STATUS = {
   IDLE: 'idle',
   CHECKING: 'checking',
   VALID: 'valid',
   INVALID: 'invalid',
   ERROR: 'error',
};

const PanelWrapper = styled.div`
   margin-top: 6px;
   padding: 8px 0 4px;
   margin-bottom: 3px;
`;
PanelWrapper.displayName = 'PanelWrapper';

const InputRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: center;
   gap: 6px;
   flex-wrap: wrap;
`;
InputRow.displayName = 'InputRow';

const TextInput = styled.input`
   flex: 1;
   min-width: 180px;
   padding: 5px 10px;
   border: 1px solid ${(p) => (p.$invalid ? '#e55353' : '#c8ced3')};
   border-radius: 4px;
   font-size: 13px;
   color: #333;

   &:focus {
      outline: none;
      border-color: ${(p) => (p.$invalid ? '#e55353' : '#20a8d8')};
   }
`;
TextInput.displayName = 'TextInput';

const ValidationIcon = styled.span`
   font-size: 15px;
   min-width: 18px;
   text-align: center;
   color: ${(p) => {
      if (p.$status === VALIDATION_STATUS.VALID) return '#27ae60';
      if (p.$status === VALIDATION_STATUS.INVALID || p.$status === VALIDATION_STATUS.ERROR) return '#e55353';
      return 'transparent';
   }};
   cursor: ${(p) =>
      p.$status === VALIDATION_STATUS.INVALID || p.$status === VALIDATION_STATUS.ERROR ? 'pointer' : 'default'};
`;
ValidationIcon.displayName = 'ValidationIcon';

const MessageArea = styled.div`
   min-height: 16px;
   margin-top: 3px;
   font-size: 11px;
   text-align: center;
   color: ${(p) => (p.$error ? '#e55353' : '#73818f')};
`;
MessageArea.displayName = 'MessageArea';

const SwitchRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: center;
   flex-wrap: wrap;
   gap: 16px;
   margin-top: 8px;
   margin-bottom: 6px;
`;
SwitchRow.displayName = 'SwitchRow';

const ToggleWrapper = styled.div`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   cursor: pointer;
   user-select: none;
`;
ToggleWrapper.displayName = 'ToggleWrapper';

const ToggleTrack = styled.span`
   position: relative;
   width: 28px;
   height: 16px;
   border-radius: 8px;
   background: ${(p) => (p.$checked ? '#20a8d8' : '#cfd8dc')};
   flex-shrink: 0;
   transition: background 0.2s;
`;
ToggleTrack.displayName = 'ToggleTrack';

const ToggleThumb = styled.span`
   position: absolute;
   top: 2px;
   left: ${(p) => (p.$checked ? '14px' : '2px')};
   width: 12px;
   height: 12px;
   border-radius: 50%;
   background: #fff;
   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
   transition: left 0.2s;
`;
ToggleThumb.displayName = 'ToggleThumb';

const ToggleLabel = styled.span`
   font-size: 12px;
   color: #73818f;
`;
ToggleLabel.displayName = 'ToggleLabel';

function getValidationIcon(status) {
   if (status === VALIDATION_STATUS.CHECKING) return '…';
   if (status === VALIDATION_STATUS.VALID) return '✓';
   if (status === VALIDATION_STATUS.INVALID || status === VALIDATION_STATUS.ERROR) return '✕';
   return '';
}

function FilterInputPanel({ activeTab, localFilter, onAdd, onRemove, orgaHash, cancelToken }) {
   const [inputValue, setInputValue] = useState('');
   const [streamNameToCheck, setStreamNameToCheck] = useState('');
   const [validation, setValidation] = useState({ status: VALIDATION_STATUS.IDLE, message: '' });
   const debounceRef = useRef(null);
   const pastedRef = useRef(false);
   const pasteTimerRef = useRef(null);
   const handleAddRef = useRef(null);

   const isStream = activeTab === 'streams';
   const isIp = activeTab === 'ips';
   const isReferrer = activeTab === 'referrers';
   const isProvider = activeTab === 'providers';
   const isCountry = activeTab === 'countries';
   const isUser = activeTab === 'users';
   const isTag = activeTab === 'tags';
   const hasLocalValidation = isStream || isIp;

   const httpHeader = useMemo(() => getHttpHeader(orgaHash), [orgaHash]);

   // Reset state when tab changes
   useEffect(() => {
      setInputValue('');
      setStreamNameToCheck('');
      setValidation({ status: VALIDATION_STATUS.IDLE, message: '' });
      pastedRef.current = false;
      clearTimeout(pasteTimerRef.current);
   }, [activeTab]);

   // Auto-add after paste + successful validation (stream, ip, token)
   useEffect(() => {
      if (
         hasLocalValidation &&
         validation.status === VALIDATION_STATUS.VALID &&
         pastedRef.current &&
         pasteTimerRef.current === null
      ) {
         pastedRef.current = false;
         handleAdd();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [validation.status]);

   // Auto-debounced stream validation
   useEffect(() => {
      if (!isStream) return;
      clearTimeout(debounceRef.current);

      const trimmed = inputValue.trim();
      if (trimmed.length < STREAM_MIN_LEN) {
         setValidation({ status: VALIDATION_STATUS.IDLE, message: '' });
         setStreamNameToCheck('');
         return;
      }

      setValidation({ status: VALIDATION_STATUS.CHECKING, message: '' });
      debounceRef.current = setTimeout(() => {
         setStreamNameToCheck(trimmed);
      }, DEBOUNCE_MS);

      return () => clearTimeout(debounceRef.current);
   }, [inputValue, isStream]);

   // Local validation for IP and Token
   useEffect(() => {
      if (!isIp) return;
      clearTimeout(debounceRef.current);

      const trimmed = inputValue.trim();
      if (!trimmed) {
         setValidation({ status: VALIDATION_STATUS.IDLE, message: '' });
         return;
      }

      setValidation({ status: VALIDATION_STATUS.CHECKING, message: '' });
      debounceRef.current = setTimeout(() => {
         try {
            const isValidIp = ipaddr.IPv4.isValidFourPartDecimal(trimmed) || ipaddr.IPv6.isValid(trimmed);
            setValidation(
               isValidIp
                  ? { status: VALIDATION_STATUS.VALID, message: '' }
                  : { status: VALIDATION_STATUS.INVALID, message: 'Not a valid IPv4 or IPv6 address' },
            );
         } catch {
            setValidation({ status: VALIDATION_STATUS.INVALID, message: 'Not a valid IPv4 or IPv6 address' });
         }
      }, DEBOUNCE_MS);

      return () => clearTimeout(debounceRef.current);
   }, [inputValue, isIp]);

   useQuery({
      enabled: isStream && streamNameToCheck !== '',
      queryKey: [streamNameToCheck, orgaHash],
      queryFn: () =>
         axios
            .post(
               '/v2/filters/validate/stream',
               { stream: streamNameToCheck },
               {
                  headers: httpHeader,
                  cancelToken: cancelToken?.token,
               },
            )
            .then((res) => res.data),
      onSuccess: (res) => {
         if (res.valid === true) {
            setValidation({ status: VALIDATION_STATUS.VALID, message: '' });
         } else {
            setValidation({
               status: VALIDATION_STATUS.INVALID,
               message: res.info ?? 'Stream not found in selected timespan',
            });
         }
      },
      onError: (err) => {
         if (!(err instanceof axios.Cancel)) {
            const error = getUnifiedErrorObject(err);
            setValidation({
               status: VALIDATION_STATUS.ERROR,
               message: error.message ?? 'Validation error',
            });
         }
      },
      retry: false,
      refetchOnWindowFocus: false,
   });

   function handleAdd() {
      const trimmed = inputValue.trim();
      if (!trimmed) return;
      if (hasLocalValidation && validation.status !== VALIDATION_STATUS.VALID) return;

      const existing = localFilter[activeTab] ?? [];
      if (existing.some((item) => item.value === trimmed)) return;

      onAdd(activeTab, trimmed);
      setInputValue('');
      setStreamNameToCheck('');
      setValidation({ status: VALIDATION_STATUS.IDLE, message: '' });
   }
   handleAddRef.current = handleAdd;

   function handleKeyDown(e) {
      if (e.key === 'Enter') handleAdd();
   }

   const isInvalid = validation.status === VALIDATION_STATUS.INVALID || validation.status === VALIDATION_STATUS.ERROR;
   const addDisabled = hasLocalValidation
      ? validation.status !== VALIDATION_STATUS.VALID
      : inputValue.trim().length === 0;

   return (
      <PanelWrapper>
         {isReferrer && (
            <SwitchRow>
               {REFERRER_SPECIAL_OPTIONS.map(({ value, label }) => {
                  const isActive = (localFilter.referrers ?? []).some((item) => item.value === value);
                  return (
                     <ToggleWrapper
                        key={value}
                        onClick={() => (isActive ? onRemove('referrers', value) : onAdd('referrers', value, label))}
                     >
                        <ToggleTrack $checked={isActive}>
                           <ToggleThumb $checked={isActive} />
                        </ToggleTrack>
                        <ToggleLabel>{label}</ToggleLabel>
                     </ToggleWrapper>
                  );
               })}
            </SwitchRow>
         )}
         <InputRow>
            <TextInput
               type='text'
               value={inputValue}
               onChange={(e) => setInputValue(e.target.value)}
               onPaste={(e) => {
                  if (hasLocalValidation) {
                     pastedRef.current = true;
                  }
                  const pasted = e.clipboardData.getData('text').trim();
                  if (pasted) {
                     clearTimeout(pasteTimerRef.current);
                     pasteTimerRef.current = setTimeout(() => {
                        pasteTimerRef.current = null;
                        if (hasLocalValidation) {
                           handleAddRef.current();
                        } else {
                           const existing = localFilter[activeTab] ?? [];
                           if (!existing.some((item) => item.value === pasted)) {
                              onAdd(activeTab, pasted);
                           }
                           setInputValue('');
                        }
                     }, 1200);
                  }
               }}
               onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                     if (pasteTimerRef.current !== null) {
                        clearTimeout(pasteTimerRef.current);
                        pasteTimerRef.current = null;
                     }
                     handleAdd();
                  } else {
                     if (pasteTimerRef.current !== null) {
                        clearTimeout(pasteTimerRef.current);
                        pasteTimerRef.current = null;
                     }
                     pastedRef.current = false;
                     handleKeyDown(e);
                  }
               }}
               placeholder={
                  isStream
                     ? `Stream name (e.g. 'FoBar-Ra1b2')`
                     : isTag
                     ? 'Bintu Tag - Enter value, confirm with Enter'
                     : 'Enter value, confirm with Enter'
               }
               $invalid={isInvalid}
               autoFocus
            />
            {hasLocalValidation && (
               <ValidationIcon
                  $status={validation.status}
                  onClick={() => {
                     if (
                        validation.status === VALIDATION_STATUS.INVALID ||
                        validation.status === VALIDATION_STATUS.ERROR
                     ) {
                        setInputValue('');
                        setStreamNameToCheck('');
                        setValidation({ status: VALIDATION_STATUS.IDLE, message: '' });
                        pastedRef.current = false;
                     }
                  }}
               >
                  {getValidationIcon(validation.status)}
               </ValidationIcon>
            )}
            <button type='button' className='btn btn-primary btn-sm' onClick={handleAdd} disabled={addDisabled}>
               + Add
            </button>
         </InputRow>
         <MessageArea $error={isInvalid}>
            {isInvalid
               ? validation.message
               : isStream && inputValue.trim().length > 0 && inputValue.trim().length < STREAM_MIN_LEN
               ? `${STREAM_MIN_LEN - inputValue.trim().length} more character(s) to start validation`
               : isProvider || isReferrer || isCountry || isUser
               ? 'Use * as wildcard, e.g. *searchterm* (case insensitive)'
               : ''}
         </MessageArea>
      </PanelWrapper>
   );
}

FilterInputPanel.propTypes = {
   activeTab: PropTypes.string.isRequired,
   localFilter: PropTypes.object.isRequired,
   onAdd: PropTypes.func.isRequired,
   onRemove: PropTypes.func.isRequired,
   orgaHash: PropTypes.string,
   cancelToken: PropTypes.object,
};

export default FilterInputPanel;
